<script setup lang="ts">
import ReportAnalyticsIcon from '@/assets/icons/ReportAnalyticsIcon.vue';
import TrashIcon from '@/assets/icons/TrashIcon.vue';
import EditIcon from '@/assets/icons/EditIcon.vue';
import ExternalLinkIcon from '@/assets/icons/ExternalLinkIcon.vue';
import { type RoundData, useRoundsStore } from '@/stores/rounds-store.ts';
import { PButton } from '@pokerly/ui';
import { cn } from '@/lib/utils.ts';
import VoteSelector from '@/views/app/session/_components/VoteSelector.vue';

interface SessionRoundCardProps {
    round: RoundData;
    active?: boolean;
}

defineProps<SessionRoundCardProps>();

async function onVoteSelected(roundId: string, vote: string | number) {
    const roundsStore = useRoundsStore();
    await roundsStore.setVote(roundId, vote);
    await roundsStore.fetch();
}
</script>

<template>
    <div :class="cn('p-4 pr-6 flex gap-2 group/round-card', active ? 'bg-gray-100 dark:bg-gray-800' : '')">
        <div class="min-w-10 h-8 text-right p-0.75">
            <span :class="cn('text-sm leading-none', active ? 'font-bold' : 'font-medium')">{{ round.order }}.</span>
        </div>
        <div class="flex flex-col grow">
            <div
                :class="
                    cn(
                        'flex items-center h-8 text-sm font-medium leading-none px-2',
                        active ? 'font-bold' : 'font-medium',
                    )
                "
            >
                <div class="flex grow items-center justify-between">
                    <span v-if="round.title">{{ round.title }}</span>
                    <span v-else>Round {{ round.order }}</span>
                    <PButton
                        v-if="round.externalLink"
                        variant="ghost"
                        size="sm"
                        class="invisible group-hover/round-card:visible"
                    >
                        <ExternalLinkIcon class="size-4" />
                    </PButton>
                </div>
            </div>
            <div v-if="round.description" class="p-2 font-medium text-sm">
                {{ round.description }}
            </div>
            <div class="flex items-center justify-between text-sm pl-2">
                <div class="flex items-center">
                    <VoteSelector @select="(vote) => onVoteSelected(round.id, vote)">
                        <PButton variant="outline" size="sm" title="Change vote">
                            <ReportAnalyticsIcon class="size-5 text-gray-400 mr-1" />
                            <span class="font-bold">{{ round.result }}</span>
                        </PButton>
                    </VoteSelector>
                    <div class="px-2 py-1 text-black/50">
                        <span
                            >{{ round.lastReplayVotes }} vote{{ round.lastReplayVotes === 1 ? '' : 's' }} |
                            {{ round.replays }} replays</span
                        >
                    </div>
                </div>
                <div v-if="false" class="items-center px-2 hidden group-hover/round-card:flex">
                    <PButton variant="ghost" size="sm" title="Delete Round">
                        <TrashIcon class="size-4" />
                    </PButton>
                    <PButton variant="ghost" size="sm" title="Edit Round">
                        <EditIcon class="size-4" />
                    </PButton>
                </div>
            </div>
        </div>
    </div>
</template>
